/**
 * useCmrCatalogs.js
 * ─────────────────────────────────────────────────────────────
 * Gestiona todos los catálogos del wizard CMR.
 *
 * Estáticos  → se cargan una sola vez al montar:
 *   clientes      getClientsCatalog()
 *   tiposServicio getServiceList()
 *   tiposVehicle  getVehicleTypesCatalog()
 *   cambios       getChangeTypes()           ← FIX: era reactive, debe ser estático
 *
 * Reactivos  → se recargan al cambiar su dep:
 *   tickets       getTicketsList(clienteId)  dep = clienteId
 *   rutas         getRoutesList(clienteId)   dep = clienteId
 *
 * Cada catálogo expone { data: [], loading: boolean, error: string|null }
 */

import { useState, useEffect } from "react";
import {
    getClientsCatalog,
    getTicketsList,
    getRoutesList,
    getServiceList,
    getVehicleTypesCatalog,
    getChangeTypes,
    getShiftsList,
} from "../../NuevaCotizacion/integracion";
import { getRecomendacionUnidad } from "../Integracion";
import { CHANGE_IDS, hasChange } from "./ChangeTypes";

// ─── HELPERS INTERNOS ─────────────────────────────────────────────────────────

const IDLE    = { data: [], loading: false, error: null };
const LOADING = { data: [], loading: true,  error: null };
const toArray = (v) => (Array.isArray(v) ? v : []);

/** Catálogos que se cargan una sola vez al montar (sin dependencias). */
function useStaticCatalog(fetchFn, errorMsg) {
    const [catalog, setCatalog] = useState(LOADING);

    useEffect(() => {
        let cancelled = false;

        fetchFn()
            .then((raw) => {
                if (!cancelled) setCatalog({ data: toArray(raw.data), loading: false, error: null });
            })
            .catch(() => {
                if (!cancelled) setCatalog({ data: [], loading: false, error: errorMsg });
            });

        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return catalog;
}

/**
* Catálogos reactivos: se recargan cuando cambian las dependencias.
* @param {Function} fetchFn
* @param {Array<any>} deps
* @param {string} errorMsg
*/
function useReactiveCatalog(fetchFn, deps, errorMsg) {
    const [catalog, setCatalog] = useState(IDLE);

    useEffect(() => {
        // si alguna dependencia viene vacía no ejecutar
        if (!deps || deps.some(d => !d)) {
            setCatalog(IDLE);
            return;
        }

        let cancelled = false;
        setCatalog(LOADING);

        fetchFn(...deps)
            .then((raw) => {
                if (!cancelled) {
                    setCatalog({
                        data: toArray(raw.data),
                        loading: false,
                        error: null
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCatalog({
                        data: [],
                        loading: false,
                        error: errorMsg
                    });
                }
            });

        return () => { cancelled = true; };
    }, deps); // ahora depende de múltiples

    return catalog;
}

// ─── HOOK PÚBLICO ─────────────────────────────────────────────────────────────

export function useCmrCatalogs(clienteId, rutaId, changeType = []) {
    // ── Estáticos ────────────────────────────────────────────────────────────
    const clientes      = useStaticCatalog(getClientsCatalog,      "No se pudo cargar los clientes.");
    const tiposServicio = useStaticCatalog(getServiceList,          "No se pudo cargar los tipos de servicio.");
    const tiposTurno    = useStaticCatalog(getShiftsList,          "No se pudo cargar los tipos de turno.");
    const tiposVehicle  = useStaticCatalog(getVehicleTypesCatalog,  "No se pudo cargar los tipos de vehículo.");
    // FIX: cambios es un catálogo fijo, no depende de ningún parámetro.
    //      Antes se usaba useReactiveCatalog con firma incorrecta (faltaba `dep`).
    const cambios       = useStaticCatalog(getChangeTypes,          "No se pudo cargar los tipos de cambio.");

    // ── Reactivos ────────────────────────────────────────────────────────────
    const tickets = useReactiveCatalog(getTicketsList, [clienteId, 5], "No se pudo cargar los tickets.");
    const rutas   = useReactiveCatalog(getRoutesList, [clienteId], "No se pudo cargar las rutas.");
    const showUnidad = hasChange(changeType, CHANGE_IDS.UNIDAD);
    const recomendacionUnidad = useReactiveCatalog(
        (rutaId) => getRecomendacionUnidad(rutaId).then(raw => ({ data: [raw.data] })),
        [showUnidad ? rutaId : null],
        "No se pudo cargar la recomendación de unidad."
    );

    return { clientes, tickets, tiposTurno, rutas, tiposServicio, tiposVehicle, cambios, recomendacionUnidad };
}
